import type { Pin, PinStorage } from "@agent-native/pinpoint";
import type { BugReportPin } from "../../lib/bug-report.js";

interface PinSenderDeps {
  /** The store instance handed to mountPinpoint — the only live seam to the
   *  queued pins: pinpoint 0.1.10 never wires `config.plugins` to
   *  `registerPlugin`, so the PinpointAPI plugin bridge is dead code in this
   *  version and `getPins()` is unreachable. */
  store: PinStorage;
  /** Widget-provided submit — calls submitBugReport with the page's note
   *  fields + this batch of pins; throws on failure so a pin stays
   *  retryable (see sendQueuedPins). */
  submit: (pins: BugReportPin[]) => Promise<void>;
  /** Resolve sourceFile:line for each pin from the live DOM before submit.
   *  Defaults to a pass-through so unit tests need no bippy. */
  enrich?: (pins: Pin[]) => Promise<Pin[]>;
}

function toBugReportPin(pin: Pin): BugReportPin {
  return {
    sourceFile: pin.framework?.sourceFile ?? null,
    componentPath: pin.framework?.componentPath ?? null,
    selector: pin.element.selector,
    textContent: pin.element.textContent ?? "",
  };
}

/**
 * Builds the `sendToAgent` handler for mountPinpoint. pinpoint awaits it
 * BEFORE clearing the store (clearOnSend), so reading the store here sees the
 * full queue of the current page — and the pinned elements are still in the
 * live DOM, which is why source enrichment runs here. Dedup by pin id because
 * pinpoint fires sendToAgent from several UI paths, not all of which clear the
 * queue.
 */
export function createPinSender({ store, submit, enrich }: PinSenderDeps) {
  const submittedIds = new Set<string>();
  return async function sendQueuedPins(): Promise<void> {
    const all = await store.list({ pageUrl: window.location.pathname });
    const fresh = all.filter((pin) => !submittedIds.has(pin.id));
    if (fresh.length === 0) return;
    const enriched = enrich ? await enrich(fresh) : fresh;
    // Let a failed submit propagate: pinpoint only runs clearOnSend when
    // sendToAgent RESOLVES, so re-throwing keeps the queued pins retryable
    // (the widget's submit surfaces the error to the reporter). Mark ids
    // submitted only on success, so a retry re-sends the same batch.
    await submit(enriched.map(toBugReportPin));
    fresh.forEach((pin) => submittedIds.add(pin.id));
  };
}
